import { useCallback, useEffect, useRef, useState } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import { Link, useNavigate } from "react-router-dom";
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import swal from "sweetalert";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import ErrorBoundary from "../../components/CustomComponents/ErrorBoundary/ErrorBoundary.jsx";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../utils/catalogTotalRecords";


import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import {
  getThemeGradientButtonStyle,
  onThemeGradientButtonEnter,
  onThemeGradientButtonLeave,
} from "../../utils/themeGradientButtonStyle";
import "./manageMarketingGroups.scss";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";

const GROUP_SORT_FIELDS = {
  groupTitle: "GroupTitle",
  customerCount: "CustomerCount",
  createdAt: "CreatedDateTime",
};

const GROUP_SORT_LABELS = {
  [GROUP_SORT_FIELDS.groupTitle]: "Group Title",
  [GROUP_SORT_FIELDS.customerCount]: "No. of Customers",
  [GROUP_SORT_FIELDS.createdAt]: "Creation Date & Time",
};

const normalizeGroup = (row) => ({
  marketingGroup_ID: row.marketingGroup_ID ?? row.MarketingGroup_ID,
  marketingGroup_Guid: row.marketingGroup_Guid ?? row.MarketingGroup_Guid ?? "",
  groupTitle: row.groupTitle ?? row.GroupTitle ?? "",
  description: row.description ?? row.Description ?? "",
  customerCount: Number(row.customerCount ?? row.CustomerCount ?? 0),
  createdDateTime: row.createdDateTime ?? row.CreatedDateTime,
});

const formatCreatedAt = (dateVal) => {
  if (!dateVal) return "—";
  try {
    const d = new Date(dateVal);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const ManageMarketingGroups = () => {
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const [search, setSearch] = useState("");

  const pageKey = "marketingGroups";
  const defaultVisibleColumns = {
    groupTitle: true,
    description: true,
    customerCount: true,
    createdAt: true,
  };
  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = {
    value: GROUP_SORT_FIELDS.createdAt,
    label: GROUP_SORT_LABELS[GROUP_SORT_FIELDS.createdAt],
  };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize && validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;
  const dispatch = useDispatch();

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const groupsFetchSeqRef = useRef(0);

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: GROUP_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  const hasFiltersApplied = () => search.trim() !== "";

  const clearFilters = () => {
    setSearch("");
    setPageNumber(1);
  };

  const fetchGroups = useCallback(async () => {
    const fetchSeq = ++groupsFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_MARKETING_GROUPS, {
        params: {
          CompID,
          Search: search?.trim() || "",
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          PageNumber: pageNumber,
          PageSize: Number(pageSize.value),
        },
      });

      if (fetchSeq !== groupsFetchSeqRef.current) return;

      const raw = response.data?.data ?? [];
      const list = (Array.isArray(raw) ? raw : []).map(normalizeGroup);
      setGroups(list);
      const total = response.data?.totalRecords ?? list.length;
      setTotalRecords(total);
      setTotalPages(response.data?.totalPages ?? Math.max(1, Math.ceil(total / Number(pageSize.value))));

      if (!hasFiltersApplied()) {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, total);
      }
    } catch {
      if (fetchSeq !== groupsFetchSeqRef.current) return;
      setGroups([]);
      setTotalRecords(0);
      showErrorToast("Failed to load marketing groups.");
    } finally {
      if (fetchSeq === groupsFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [CompID, search, sortBy.value, sortOrder.value, pageNumber, pageSize.value]);

  const handleDelete = async (group) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Delete marketing group "${group.groupTitle}"?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirmDelete) return;

    const groupGuid = group.marketingGroup_Guid ?? group.MarketingGroup_Guid;
    if (!groupGuid) {
      showErrorToast("Marketing group identifier is missing.");
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.delete(
        APIs.DELETE_MARKETING_GROUP(groupGuid),
        { params: { CompID } }
      );
      if (response.data?.status) {
        showSuccessToast("Marketing group deleted successfully.");
        fetchGroups();
      } else {
        showErrorToast(response.data?.message || "Delete failed.");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
      dispatch(
        setVisibleColumns({
          pageKey,
          columns: mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns),
        })
      );
    }
    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    } else if (preferences.sortBy?.value === "MarketingGroup_ID") {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(groupsFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchGroups(), [fetchGroups]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy?.value, sortOrder?.value, pageNumber]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const isUnfilteredView = !hasFiltersApplied();
  const showEmptyCatalogState =
    initialLoadComplete && !loading && isUnfilteredView && catalogTotalRecords === 0;
  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No marketing groups match your search "${search.trim()}". Try different keywords or clear filters.`;
    }
    return "There are no marketing groups yet. Create a new group to start building audiences.";
  };

  const renderNoDataMessage = () => {
    if (loading || groups.length > 0) return null;
    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No marketing groups found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  const gradientBtnStyle = getThemeGradientButtonStyle(themeColor);

  return (
    <ErrorBoundary>
      <div>
        <div className="d-flex align-items-center mb-3">
          <div>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="#/">Home</a>
              </li>
              <li className="breadcrumb-item active">Manage Marketing Groups</li>
            </ol>
            <h1 className="page-header mb-0">Marketing Groups</h1>
          </div>

          <div className="ms-auto">
            <Link to="/marketing/addMarketingGroup">
              <button
                type="button"
                className="btn px-4"
                style={gradientBtnStyle}
                onMouseEnter={(e) => onThemeGradientButtonEnter(e, themeColor)}
                onMouseLeave={(e) => onThemeGradientButtonLeave(e, themeColor)}
              >
                <i className="fa fa-plus fa-lg me-2 ms-n2 text-white" />
                Create New Group
              </button>
            </Link>
          </div>
        </div>

        <div className="mb-3 mt-2">
          {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

          {showEmptyCatalogState && (
            <AddButton
              onClick={() => navigate("/marketing/addMarketingGroup")}
              title="Create New Group"
            />
          )}

          {showGridToolbar && (
            <ModernGridToolbar
              isDarkMode={isDarkMode}
              title="Marketing Groups"
              totalCount={totalRecords}
              searchValue={search}
              onSearchChange={setSearch}
              searchPlaceholder="Group title"
              bodyFlush
              headerControls={
                <ColumnSelection
                  visibleColumns={visibleColumns}
                  setVisibleColumns={(cols) =>
                    dispatch(setVisibleColumns({ pageKey, columns: cols }))
                  }
                />
              }
            >
              <div className="px-3 pb-3">
                <GridSectionLoader loading={loading} minHeight="280px">
                  {renderNoDataMessage()}

                  {!loading && groups.length > 0 && (
                    <div
                      className="table-responsive table-container"
                      style={{
                        minHeight: "200px",
                        maxHeight: "450px",
                        overflowY: "auto",
                        overflowX: "auto",
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      <table
                        className="table table-hover manage-marketing-groups-table"
                        style={{ "--modern-grid-sort-active": themeColor }}
                      >
                        <thead
                          className="bg-light sticky-header"
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 10000,
                            backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                            color: isDarkMode ? "#ffffff" : "#000000",
                          }}
                        >
                          <tr>
                            {visibleColumns.groupTitle && (
                              <SortableColumnHeader
                                sortField={GROUP_SORT_FIELDS.groupTitle}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={handleColumnSort}
                              >
                                Group Title
                              </SortableColumnHeader>
                            )}
                            {visibleColumns.description && (
                              <th className="pt-2 pb-2">Description</th>
                            )}
                            {visibleColumns.customerCount && (
                              <SortableColumnHeader
                                sortField={GROUP_SORT_FIELDS.customerCount}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={handleColumnSort}
                                className="text-center"
                              >
                                No. of Customers
                              </SortableColumnHeader>
                            )}
                            {visibleColumns.createdAt && (
                              <SortableColumnHeader
                                sortField={GROUP_SORT_FIELDS.createdAt}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={handleColumnSort}
                              >
                                Creation Date &amp; Time
                              </SortableColumnHeader>
                            )}
                            <th className="pt-2 pb-2 text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groups.map((group) => (
                            <tr
                              key={group.marketingGroup_ID}
                              onClick={() =>
                                navigate(
                                  `/marketing/updateMarketingGroup/${group.marketingGroup_ID}`
                                )
                              }
                              style={{ cursor: "pointer" }}
                            >
                              {visibleColumns.groupTitle && (
                                <td className="align-middle fw-semibold">
                                  {group.groupTitle}
                                </td>
                              )}
                              {visibleColumns.description && (
                                <td
                                  className="align-middle"
                                  title={group.description || ""}
                                >
                                  {group.description
                                    ? group.description.length > 80
                                      ? `${group.description.slice(0, 80)}…`
                                      : group.description
                                    : "—"}
                                </td>
                              )}
                              {visibleColumns.customerCount && (
                                <td className="align-middle text-center">
                                  {group.customerCount}
                                </td>
                              )}
                              {visibleColumns.createdAt && (
                                <td className="align-middle">
                                  {formatCreatedAt(group.createdDateTime)}
                                </td>
                              )}
                              <td
                                className="align-middle text-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <TableActionsMenu menuEnd menuStyle={{ zIndex: 1050 }}>
                                  <TableActionsItem
                                    as={Link}
                                    to={`/marketing/updateMarketingGroup/${group.marketingGroup_ID}`}
                                    icon="fa fa-edit"
                                  >
                                    Edit
                                  </TableActionsItem>
                                  <TableActionsItem
                                    danger
                                    icon="fa fa-trash"
                                    onClick={() => handleDelete(group)}
                                  >
                                    Delete
                                  </TableActionsItem>
                                </TableActionsMenu>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <ModernGridPagerFooter
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    totalRecords={totalRecords}
                    pageNumber={pageNumber}
                    totalPages={totalPages}
                    setPageNumber={setPageNumber}
                  />
                </GridSectionLoader>
              </div>
            </ModernGridToolbar>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default ManageMarketingGroups;
